import type { IssueDefinition } from "../../issue-definition";
import { ISSUE_CATEGORY } from "../../issue-enums";
import { ISSUE_OPERATOR_ACTION } from "../../issue-operator-action";

/**
 * The backup exists in-region and is NOT reaching the DR vault — a separate
 * code, and the one this whole monitor was written for.
 *
 * 🔴 IT IS SEPARATE BECAUSE THE FAILURE IS INVISIBLE FROM EVERY OTHER ANGLE.
 * AWS Backup validates a copy destination only when a job actually RUNS, so a
 * malformed or unreachable destination vault is not rejected when the plan is
 * provisioned. It surfaces as nightly copy jobs that fail — and nothing else
 * changes. The plan exists, the console looks configured, the in-region vault
 * fills up on schedule, and the DR vault silently never receives anything. A
 * tenant in this state reads as fully protected on every surface except this
 * one.
 *
 * Folding it into the code above would make "backups stopped" and "backups
 * are not leaving the region" the same sentence. They have different fixes and
 * very different blast radii: the first loses the last day, the second means
 * a regional loss takes the tenant's entire git history with it.
 */
export const VIBE_TENANT_DR_COPY_STALE = {
  category: ISSUE_CATEGORY.OBSERVABILITY,
  message:
    "This tenant's Forgejo git-repo volume is being backed up in its own region, but no recent copy of those backups has landed in the cross-region DR vault. The tenant is protected against losing the volume and NOT protected against losing the region. This failure is invisible everywhere else: AWS Backup validates a copy destination only when a job runs, so a bad destination is accepted at provision time and then surfaces only as nightly copy jobs that quietly fail while the plan, the console and the in-region vault all look healthy. Raised per tenant and cleared once a recent copy is present in the DR vault.",
  severity: "critical",
  remedy:
    "Run the backup now. When a completed local recovery point already exists, this also starts an on-demand cross-region copy to the DR vault directly — the same StartCopyJob the nightly plan's copy action would run. If no local point exists yet, this starts one; run the lever again once it lands to trigger the copy.",
  operatorAction: ISSUE_OPERATOR_ACTION.VIBE_RUN_TENANT_BACKUP_NOW
} as const satisfies IssueDefinition;
